import { AbstractHistogram } from 'hdr-histogram-js'
import * as moment from 'moment'
import { importHdrHistogram } from '../../helper/importHdrHistogram'
import { BenchmarkThresholds } from '../Benchmark'
import { Report } from '../Report'
import { SerializedHistogram } from '../SerializedHistogram'
import { TraceList } from '../TraceSummary'
import { AggregatedError } from './AggregatedError'
import { calcDays, Granularity } from './HistoryCriteria'
import { layersLayerExists } from '../Layers'

export interface TransactionReport<HISTOGRAM> {
    active: boolean
    name: string
    identifier: string
    benchmark?: BenchmarkThresholds | null
    worst_response: number
    average_response: number
    memory: number
    impact: number
    requests: number
    max: number
    average: number
    errors: number
    histogram: HISTOGRAM | null
}

export interface ServiceReport<HISTOGRAM> {
    day: string
    environmentName: string
    serviceName: string
    report: Report<HISTOGRAM>
    errors: AggregatedError[]
    transactions: { [key: string]: TransactionReport<HISTOGRAM> }
    profiles: number
    traces: number
    archivedTraces?: TraceList
}

export interface AggregatedServiceReport extends ServiceReport<AbstractHistogram> {
    noData: boolean
    days: string[]
    granularity: Granularity
}

export const mergeServiceReports = (
    reports: Array<ServiceReport<AbstractHistogram>>,
    date: moment.Moment,
    granularity: Granularity,
): AggregatedServiceReport => {
    const concatItems = ['averageResponseTimes', 'percentileResponseTimes', 'requests', 'errors', 'downstream', 'memory']
    const days = calcDays(date, granularity).sort()

    if (reports.length === 0) {
        // @ts-ignore
        return { days, noData: true }
    }

    // we need to sort service-reports to aggregate cleanly
    reports.sort((a: ServiceReport<AbstractHistogram>, b: ServiceReport<AbstractHistogram>) => a.day.localeCompare(b.day))

    const [first, ...rest] = reports

    const aggregatedReport: AggregatedServiceReport = {
        ...first,
        days,
        granularity,
        noData: false,
    }

    // @todo this should be reduce() instead of map
    rest.map((report: ServiceReport<AbstractHistogram>) => {
        concatItems.map(item => {
            aggregatedReport.report[item] = {
                ...aggregatedReport.report[item],
                ...report.report[item],
            }
        })

        if (!aggregatedReport.archivedTraces) {
            aggregatedReport.archivedTraces = report.archivedTraces
        } else if (report.archivedTraces) {
            aggregatedReport.archivedTraces = {
                ...aggregatedReport.archivedTraces,
                total: aggregatedReport.archivedTraces.total + report.archivedTraces.total,
                profiles: [...aggregatedReport.archivedTraces.profiles, ...report.archivedTraces.profiles],
            }
        }

        aggregatedReport.report.hasDownstream = aggregatedReport.report.hasDownstream || report.report.hasDownstream

        if (!aggregatedReport.report.histogram && report.report.histogram) {
            aggregatedReport.report.histogram = report.report.histogram
        } else if (aggregatedReport.report.histogram && report.report.histogram) {
            aggregatedReport.report.histogram.add(report.report.histogram)
        }

        Object.keys(report.report.slices).map(key => {
            const sliceCurrent = report.report.slices[key]
            const sliceAggregated = aggregatedReport.report.slices[key] || null

            if (!sliceAggregated) {
                aggregatedReport.report.slices[key] = report.report.slices[key]
                return
            }

            sliceAggregated.average =
                (sliceAggregated.average * sliceAggregated.requests + sliceCurrent.average * sliceCurrent.requests) /
                (sliceAggregated.requests + sliceCurrent.requests)

            sliceAggregated.responseTime =
                (sliceAggregated.responseTime * sliceAggregated.requests + sliceCurrent.responseTime * sliceCurrent.requests) /
                (sliceAggregated.requests + sliceCurrent.requests)

            sliceAggregated.memory = Math.max(sliceAggregated.memory || 0, sliceCurrent.memory || 0)
            const errorsAggregated = sliceAggregated.errorRate * sliceAggregated.requests
            const errorsCurrent = sliceCurrent.errorRate * sliceCurrent.requests
            sliceAggregated.errorRate =
                errorsCurrent + errorsAggregated === 0
                    ? 0
                    : (errorsAggregated + errorsCurrent) / (sliceAggregated.requests + sliceCurrent.requests)

            Object.keys(sliceCurrent.downstream).forEach(layer => {
                if (!layersLayerExists(layer)) {
                    return
                }

                const currentLayer = sliceCurrent.downstream[layer] || 0
                const aggregatedLayer = sliceAggregated.downstream[layer] || 0
                sliceAggregated.downstream[layer] =
                    (aggregatedLayer * sliceAggregated.requests + currentLayer * sliceCurrent.requests) /
                    (sliceAggregated.requests + sliceCurrent.requests)
            })

            sliceAggregated.pageCache.hits += sliceCurrent.pageCache.hits
            sliceAggregated.pageCache.misses += sliceCurrent.pageCache.misses
            sliceAggregated.crawlers += sliceCurrent.crawlers

            // do this last
            sliceAggregated.requests += sliceCurrent.requests
        })

        Object.keys(report.transactions).map(id => {
            if (!aggregatedReport.transactions[id]) {
                const transaction = {
                    ...report.transactions[id],
                    active: (report.transactions[id] as { active?: boolean }).active ?? true,
                }
                aggregatedReport.transactions[id] = transaction

                return
            }

            aggregatedReport.transactions[id] = mergeTransactionReport(
                aggregatedReport.transactions[id],
                report.transactions[id],
                aggregatedReport.report.slices[aggregatedReport.serviceName].requests -
                    report.report.slices[aggregatedReport.serviceName].requests,
                report.report.slices[report.serviceName].requests,
            )
        })

        aggregatedReport.errors = report.errors.reduce((aggregate: AggregatedError[], current: AggregatedError) => {
            let newError = true
            aggregate = aggregate.map(error => {
                if (error.errorId !== current.errorId) {
                    return error
                }

                newError = false

                return {
                    ...error,
                    occurrences: error.occurrences + current.occurrences,
                }
            })

            if (newError) {
                aggregate.push(current)
            }

            return aggregate
        }, aggregatedReport.errors)

        // aggregatedReport.memory = 0
        // Object.keys(report.report.slices).map(key => {
        //     const sliceAggregated = aggregatedReport.report.slices[key] || null
        //     aggregatedReport.memory =
        //         sliceAggregated.memory && aggregatedReport.memory ? Math.max(aggregatedReport.memory, sliceAggregated.memory) : 0
        // })
    })

    aggregatedReport.errors.sort((a: AggregatedError, b: AggregatedError) => b.occurrences - a.occurrences)

    return aggregatedReport
}

const mergeTransactionReport = (
    first: TransactionReport<AbstractHistogram>,
    second: TransactionReport<AbstractHistogram>,
    totalRequestsFirst: number,
    totalRequestsSecond: number,
): TransactionReport<AbstractHistogram> => {
    // @todo TX histograms are currently not used here
    // if (!first.histogram && second.histogram) {
    //     first.histogram = second.histogram
    // } else if (first.histogram && second.histogram) {
    //     first.histogram.add(second.histogram)
    // }

    const averageWeightedByTotalRequests = (firstValue: number, secondValue: number) =>
        (firstValue * totalRequestsFirst + secondValue * totalRequestsSecond) / (totalRequestsFirst + totalRequestsSecond)
    const averageWeightedByTransactionRequests = (firstValue: number, secondValue: number) =>
        (firstValue * first.requests + secondValue * second.requests) / (first.requests + second.requests)

    first.impact = averageWeightedByTotalRequests(first.impact, second.impact)

    first.memory = averageWeightedByTransactionRequests(first.memory, second.memory)
    first.worst_response = averageWeightedByTransactionRequests(first.worst_response, second.worst_response)
    first.average_response = averageWeightedByTransactionRequests(first.average_response, second.average_response)
    first.average = averageWeightedByTransactionRequests(first.average, second.average)
    first.max = Math.max(first.max, second.max)

    first.errors += second.errors
    first.requests += second.requests

    return { ...first }
}

export const convertHistograms = (report: ServiceReport<SerializedHistogram>): ServiceReport<AbstractHistogram> => {
    const transactions = {}
    Object.keys(report.transactions).map(key => {
        transactions[key] = {
            ...report.transactions[key],
            histogram: importHdrHistogram(report.transactions[key].histogram),
        }
    })

    return {
        ...report,
        report: {
            ...report.report,
            histogram: importHdrHistogram(report.report.histogram),
        },
        transactions,
    }
}
